export const M10_UNIT_11 = {
  id: "m10-u11-reporting-verbs-infinitive",
  title: "Unit 11: Telling People What To Do",
  topic: "Reporting Verbs + Object + Infinitive",
  iconType: "users",
  grammarImage: "/m10-u11-reporting-verbs-infinitive.jpg",
  grammarText: "Unit focus: Seven reporting verbs that take an object and infinitive — asked, reminded, warned, ordered, encouraged, advised, persuaded. Each verb reveals something different about the relationship between speaker and listener, and the tone of the instruction given.",

  sets: [

    // ===================================================================
    // SET 1: ASKED (B1+)
    // ===================================================================
    {
      id: "m10-u11-set1",
      title: "Set 1: The First Request",
      context: `*Accra, pre-season Monday — Coach Amara's first team meeting*

Coach Amara is 44 and has just taken over a struggling football club.
She had a reputation for transforming teams — not by shouting,
but by asking the right things of the right people.
Her first words to the squad were not an order. They were a request.

*Accra, lunes de pretemporada — Primera reunión del equipo de la entrenadora Amara*

La entrenadora Amara tiene 44 años y acaba de hacerse cargo de un club de fútbol en dificultades.
Tenía fama de transformar equipos — no a gritos,
sino pidiendo las cosas correctas a las personas correctas.
Sus primeras palabras al equipo no fueron una orden. Fueron una petición.`,

      questions: [
        {
          type: "match",
          q: "Match the structure of 'asked' as a reporting verb:",
          pairs: [
            { left: "structure", right: "asked + person + to + infinitive" },
            { left: "negative form", right: "asked + person + NOT to + infinitive" },
            { left: "asked vs told", right: "Asked = polite request. Told = direct instruction." }
          ],
          a: "completed",
          successTip: "ASKED = polite — the person could theoretically say no. TOLD = more direct. 'She asked them to stay' (request). 'She told them to stay' (instruction). The verb reveals the power dynamic! 🤝"
        },

        {
          type: "mc",
          q: "Coach Amara: 'Please arrive fifteen minutes early tomorrow.' → She ___ the players ___ fifteen minutes early the next day.",
          options: ["asked / to arrive", "asked / arrive", "told / to arriving"],
          a: "asked / to arrive",
          tip: "Asked + person + to + infinitive. Please = polite = asked. Tomorrow → the next day (time shift).",
          successTip: "Three things at once! ASKED (polite), TO ARRIVE (infinitive — not arrive or arriving), THE NEXT DAY (time shift from tomorrow). All three must be right! 🎯"
        },

        {
          type: "mc",
          q: "She ___ the captain ___ a short speech before training — but the captain had never spoken in front of the team before.",
          options: ["asked / to give", "ordered / to give", "reminded / to give"],
          a: "asked / to give",
          tip: "The captain could refuse (nervous, no experience) = polite request = asked.",
          successTip: "Context reveals the verb! The captain's nervousness tells us this was a REQUEST — she could have said no. If Amara had the authority to force it, ORDERED. But she asked! 🎭"
        },

        {
          type: "mc",
          q: "Which sentence contains an error?",
          options: [
            "She asked them not to use their phones during meetings.",
            "She asked them to not use their phones during meetings.",
            "She asked not them to use their phones during meetings."
          ],
          a: "She asked not them to use their phones during meetings.",
          tip: "NOT comes after the person, before the infinitive. asked + person + NOT to + infinitive.",
          successTip: "Word order matters! ASKED + THEM + NOT TO + USE. The person (them) always comes before NOT. 'She asked not them' ❌ — NOT must sit between the person and the infinitive! 📌"
        },

        {
          type: "mc",
          q: "Coach Amara had been ___ the squad ___ more during training for weeks before the season started.",
          options: ["asking / to communicate", "asking / communicate", "asked / to communicate"],
          a: "asking / to communicate",
          tip: "Past Perfect Continuous context = had been asking. To communicate = infinitive form.",
          successTip: "HAD BEEN ASKING = ongoing request over weeks before season. The infinitive (to communicate) never changes — only the surrounding tense shifts! ⏪"
        },

        {
          type: "input",
          q: "Amara to the goalkeeper: 'Don't shout at your defenders.' → She asked the goalkeeper ___ ___ ___ at the defenders. (negative form)",
          a: "not to shout",
          alts: ["Not to shout"],
          tip: "Asked + person + not to + infinitive. Negative command = not to."
        },

        {
          type: "mc",
          q: "Yuki was covering the story. She wrote: 'Coach Amara ___ her players ___ each other by first name — a small but significant cultural shift.'",
          options: ["asked / to address", "ordered / to address", "persuaded / address"],
          a: "asked / to address",
          tip: "Cultural shift requested gently = asked. No authority force needed here.",
          successTip: "'Small but significant' — gentle change = ASKED. If it had been a rule, ORDERED. If players resisted, PERSUADED. The adverb 'gently' signals the verb! 🌱"
        },

        {
          type: "mc",
          q: "Marcus interviewed Amara. She said: 'I never order what I can ask for.' → She told Marcus she never ___ what she ___ ___ for.",
          options: ["ordered / could ask", "asked / could order", "ordered / had asked"],
          a: "ordered / could ask",
          tip: "Her philosophy: never order what you can ask. Could = backshift of can.",
          successTip: "Can → could (backshift). Her leadership philosophy in one sentence — ORDERED (forced) vs ASKED (requested). The whole unit's contrast in one quote! 🏆"
        },

        {
          type: "order",
          q: "Reorder: 'Ella les pidió a los jugadores que llegaran pronto y que no usaran sus teléfonos durante las reuniones.'",
          words: ["She", "asked", "the", "players", "to", "arrive", "early", "and", "not", "to", "use", "their", "phones", "during", "meetings."],
          a: "She asked the players to arrive early and not to use their phones during meetings.",
          extra: ["ordered", "arriving", "told"]
        },

        {
          type: "input",
          q: "Season report: 'From day one, Coach Amara ___ everyone ___ differently — and almost everyone did.' (ask — past simple + to + try)",
          a: "asked / to try",
          alts: ["Asked / to try"],
          successTip: "ASKED TO TRY — the simplest and most powerful leadership request. Not forced, not pressured — just asked. And almost everyone responded. That's the power of the right verb! 🤝✅"
        }
      ]
    },

    // ===================================================================
    // SET 2: REMINDED (B1+)
    // ===================================================================
    {
      id: "m10-u11-set2",
      title: "Set 2: Don't Forget",
      context: `*Accra, Wednesday — pre-match briefing*

Coach Amara had been working with this squad for three weeks.
They had learned the system. They knew the rules. They knew the warm-up.
But knowing something and doing it under pressure are different things.
Before every match, Amara reminded them — not to teach, but to reinforce.

*Accra, miércoles — charla previa al partido*

La entrenadora Amara llevaba tres semanas trabajando con este equipo.
Habían aprendido el sistema. Conocían las reglas. Conocían el calentamiento.
Pero saber algo y hacerlo bajo presión son cosas distintas.
Antes de cada partido, Amara les recordaba — no para enseñar, sino para reforzar.`,

      questions: [
        {
          type: "match",
          q: "Match 'reminded' to its meaning and contrast:",
          pairs: [
            { left: "reminded", right: "recordó (ya lo sabían — refuerzo de conocimiento previo)" },
            { left: "reminded vs asked", right: "Reminded = they already knew. Asked = new request." },
            { left: "reminded vs told", right: "Reminded = prior knowledge. Told = new information/instruction." }
          ],
          a: "completed",
          successTip: "REMINDED = they already knew! If you use REMINDED, you're saying the person had this information before. 'She reminded them to warm up' = they knew, they just needed the nudge! 🔔"
        },

        {
          type: "mc",
          q: "Amara: 'Don't forget — the warm-up is non-negotiable.' → She ___ the squad ___ ___ the warm-up.",
          options: ["reminded / not to skip", "asked / not to skip", "warned / to do"],
          a: "reminded / not to skip",
          tip: "They already knew the rule = reminded. Don't forget = not to skip (negative infinitive).",
          successTip: "'Don't forget' = the classic signal for REMINDED! They KNEW. She's nudging their existing memory. NOT TO SKIP = negative infinitive. Two decisions, one sentence! 🔔"
        },

        {
          type: "mc",
          q: "Choose the correct verb: 'Léa had been told the offside rule many times. Before the match, Amara ___ her ___ track of her position.'",
          options: ["reminded / to keep", "asked / to keep", "advised / to keep"],
          a: "reminded / to keep",
          tip: "Had been told many times = prior knowledge = reminded.",
          successTip: "'Had been told many times' = the key context clue for REMINDED! Prior knowledge present → reminded is the only correct choice! 🎓"
        },

        {
          type: "mc",
          q: "Amara ___ the captain ___ call for the ball clearly — something she ___ already ___ him ___ do since day one.",
          options: ["reminded / to / had / asked / to", "asked / to / had / reminded / to", "reminded / to / had / told / to"],
          a: "reminded / to / had / asked / to",
          tip: "Pre-match nudge = reminded. Original day-one request = asked (polite, first time). Had asked = Past Perfect (earlier action).",
          successTip: "Timeline of verbs! First she ASKED (day one, new request). Now she REMINDS (week three, reinforcing known rule). HAD ASKED = Past Perfect — earlier than the reminded! ⏪"
        },

        {
          type: "mc",
          q: "Which sentence uses 'reminded' incorrectly?",
          options: [
            "She reminded them not to argue with the referee.",
            "She reminded them that arguing with the referee was not allowed.",
            "She reminded them the rule about arguing for the first time."
          ],
          a: "She reminded them the rule about arguing for the first time.",
          tip: "If it's the FIRST time, you can't remind — you tell or explain. Reminded = prior knowledge.",
          successTip: "Error detection! 'For the first time' destroys the meaning of REMINDED — you can only remind someone of something they ALREADY KNOW. First time = told or explained! ❌🔔"
        },

        {
          type: "input",
          q: "Pre-match routine: Amara always ___ the squad ___ focused and ___ ___ talk back to officials. (remind — past simple + to stay / not to)",
          a: "reminded / to stay / not to",
          alts: ["Reminded / to stay / not to"],
          tip: "Reminded + to stay (positive) + not to (negative — implied 'talk back'). Both forms in one sentence."
        },

        {
          type: "mc",
          q: "Yuki noted: 'What made Amara special was that she never ___ players ___ things they didn't already know — she always ___ them ___ what they had already learned.'",
          options: ["reminded / to do / asked / to apply", "asked / to do / reminded / to apply", "told / to do / reminded / to apply"],
          a: "asked / to do / reminded / to apply",
          tip: "New things = asked. Already learned = reminded. The distinction in one observation!",
          successTip: "ASKED (new things — polite request) vs REMINDED (already learned — reinforcement). Yuki spotted the difference that made Amara exceptional! That's the whole verb contrast! 🌟"
        },

        {
          type: "mc",
          q: "Dr. Santos ___ Léa ___ ___ on the injured leg before the medical team ___ cleared her to play.",
          options: ["reminded / not to train / had", "warned / not to train / had", "asked / not to train / has"],
          a: "warned / not to train / had",
          tip: "Injury = danger = warned. Not to train = negative infinitive. Had cleared = Past Perfect (before current moment).",
          successTip: "Context shift! Injury = DANGER = WARNED, not reminded! If she already knew not to train, REMINDED. But injury risk = WARNED. Had cleared = Past Perfect. Three decisions! 🏥"
        },

        {
          type: "order",
          q: "Reorder: 'Ella les recordó a los jugadores que mantuvieran la concentración y que no protestaran al árbitro.'",
          words: ["She", "reminded", "the", "players", "to", "stay", "focused", "and", "not", "to", "argue", "with", "the", "referee."],
          a: "She reminded the players to stay focused and not to argue with the referee.",
          extra: ["asked", "warned", "staying"]
        },

        {
          type: "input",
          q: "Season summary: 'Before every match, Amara ___ her team ___ the basics — not because they had forgotten, but because pressure makes people forget.' (remind — past simple + to trust)",
          a: "reminded / to trust",
          alts: ["Reminded / to trust"],
          successTip: "REMINDED TO TRUST — beautiful and precise! They knew how to play. They just needed reminding under pressure. REMINDED = prior knowledge + reinforcement. The verb is perfect! 🔔✅"
        }
      ]
    },

    // ===================================================================
    // SET 3: WARNED (B2-)
    // ===================================================================
    {
      id: "m10-u11-set3",
      title: "Set 3: Don't Do That",
      context: `*Accra Sports Clinic, Thursday — Dr. Santos with Léa*

Léa is 23, French, and the most talented striker in the squad.
She was also the most likely to ignore medical advice.
Dr. Santos had been working with elite athletes for ten years.
He had learned that the most important words in sports medicine were always: "Don't."

*Clínica deportiva de Accra, jueves — Dr. Santos con Léa*

Léa tiene 23 años, es francesa y es la delantera más talentosa del equipo.
También era la más propensa a ignorar los consejos médicos.
El Dr. Santos llevaba diez años trabajando con atletas de élite.
Había aprendido que las palabras más importantes en medicina deportiva siempre eran: "No."`,

      questions: [
        {
          type: "match",
          q: "Match 'warned' to its meaning and structure:",
          pairs: [
            { left: "warned", right: "advirtió (peligro o consecuencia negativa implícita)" },
            { left: "warned + not to", right: "warned + person + not to + infinitive" },
            { left: "warned vs reminded", right: "Warned = danger/consequence. Reminded = prior knowledge nudge." }
          ],
          a: "completed",
          successTip: "WARNED = there's a CONSEQUENCE if you don't listen! 'She warned him not to run' implies: if you run, something bad will happen. The danger is always behind a WARNING! ⚠️"
        },

        {
          type: "mc",
          q: "Dr. Santos: 'Don't push through the pain — you could make it much worse.' → He ___ Léa ___ ___ through the pain.",
          options: ["warned / not to push", "reminded / not to push", "asked / not to push"],
          a: "warned / not to push",
          tip: "Danger + consequence ('could make it worse') = warned. Don't = not to. Push → push (infinitive).",
          successTip: "'Could make it worse' = the consequence signal for WARNED! Whenever there's a danger or negative outcome implied, WARNED is the verb! ⚠️"
        },

        {
          type: "mc",
          q: "He ___ her ___ ___ on the pitch until the scan results ___ back.",
          options: ["warned / not to go / came", "reminded / not to go / had come", "asked / to wait / came"],
          a: "warned / not to go / came",
          tip: "Medical prohibition with consequence = warned. Not to go = negative infinitive. Came = backshifted from come.",
          successTip: "WARNED + NOT TO GO (prohibition with danger) + CAME (backshift of come). Three layers! The danger is clear — going on pitch before scan = risk of serious injury! ⚠️🏥"
        },

        {
          type: "mc",
          q: "Choose the sentence that uses 'warned' correctly:",
          options: [
            "He warned her not to train on the injured muscle.",
            "He warned her to not train on the injured muscle.",
            "He warned not her to train on the injured muscle."
          ],
          a: "He warned her not to train on the injured muscle.",
          tip: "Warned + person + NOT TO + infinitive. NOT comes after the person, before TO.",
          successTip: "Word order again! WARNED + HER + NOT TO + TRAIN. 'To not train' ❌ (split infinitive — avoid), 'not her to train' ❌ (person displaced). Only Option 1 is correct! 📌"
        },

        {
          type: "mc",
          q: "Dr. Santos ___ Amara ___ Léa ___ train that week, explaining that the risk of permanent injury ___ significant.",
          options: ["warned / not to let / was", "asked / to let / had been", "reminded / not to let / was"],
          a: "warned / not to let / was",
          tip: "Permanent injury risk = warned. Not to let = negative infinitive (don't let her). Was = backshift of is.",
          successTip: "Warned Amara NOT TO LET Léa train — double layer! Warned (danger) + not to let (negative infinitive applied to a third person). Was = backshift. Three decisions! 🏥"
        },

        {
          type: "input",
          q: "Medical report: 'Dr. Santos had ___ Léa ___ ___ the squad session — but she went anyway.' (warn — past perfect + join)",
          a: "warned / not to join",
          alts: ["Warned / not to join"],
          tip: "Had warned = Past Perfect (before she went). Not to join = negative infinitive.",
          successTip: "HAD WARNED = Past Perfect — she was warned BEFORE she went anyway. The Past Perfect shows the warning came first. NOT TO JOIN = negative infinitive. Classic WARNED structure! ⚠️"
        },

        {
          type: "mc",
          q: "Amara ___ the whole squad ___ ___ tackles in training — she ___ them ___ sharp but careful.",
          options: ["warned / not to overdo / asked / to stay", "reminded / to avoid / warned / not to", "asked / not to do / reminded / to stay"],
          a: "warned / not to overdo / asked / to stay",
          tip: "Physical danger = warned + not to. Safe training request = asked + to. Two different verbs, two different tones.",
          successTip: "WARNED (danger: don't overdo it) + ASKED (positive request: stay sharp but careful). Same conversation, two verbs because two different messages. Real coaching! ⚽"
        },

        {
          type: "mc",
          q: "Marcus wrote: 'The physio ___ the striker ___ ___ to the club doctor before making any decision about returning to play.'",
          options: ["warned / not to return / consulting", "warned / not to return / without consulting", "advised / to speak / before"],
          a: "warned / not to return / without consulting",
          tip: "Risk of returning too early = warned. Not to return without consulting = the full negative structure.",
          successTip: "WARNED + NOT TO RETURN WITHOUT CONSULTING — the full complex negative. This is how warnings work in real professional contexts! Not just 'don't do X' but 'don't do X without doing Y'! ⚠️🏥"
        },

        {
          type: "order",
          q: "Reorder: 'El médico advirtió a Léa que no entrenara con el equipo y que no ignorara el dolor.'",
          words: ["The", "doctor", "warned", "Léa", "not", "to", "train", "with", "the", "squad", "and", "not", "to", "ignore", "the", "pain."],
          a: "The doctor warned Léa not to train with the squad and not to ignore the pain.",
          extra: ["reminded", "asking", "told"]
        },

        {
          type: "input",
          q: "Léa's diary: 'Dr. Santos ___ me ___ ___ on it — he said I could end my career.' (warn — past simple + not to walk)",
          a: "warned / not to walk",
          alts: ["Warned / not to walk"],
          successTip: "WARNED NOT TO WALK — with 'could end my career' as the implied consequence. Even without the consequence stated in the reported verb sentence, WARNED carries it! ⚠️✅"
        }
      ]
    },

    // ===================================================================
    // SET 4: ORDERED (B2-)
    // ===================================================================
    {
      id: "m10-u11-set4",
      title: "Set 4: No Discussion",
      context: `*Accra, match day — 70th minute, losing 2-1*

Coach Amara had tried asking. She had tried encouraging.
The team was playing well but not well enough.
With twenty minutes left and a trophy on the line,
something in her voice changed. This was no longer a request.

*Accra, día del partido — minuto 70, perdiendo 2-1*

La entrenadora Amara había intentado pedir. Había intentado animar.
El equipo jugaba bien, pero no lo suficiente.
Con veinte minutos por delante y un trofeo en juego,
algo en su voz cambió. Esto ya no era una petición.`,

      questions: [
        {
          type: "match",
          q: "Match 'ordered' to its meaning and contrast:",
          pairs: [
            { left: "ordered", right: "ordenó (autoridad jerárquica — no es opcional)" },
            { left: "ordered vs asked", right: "Ordered = cannot refuse. Asked = could refuse." },
            { left: "ordered vs warned", right: "Ordered = do this now. Warned = danger if you don't." }
          ],
          a: "completed",
          successTip: "ORDERED = authority + no option to refuse. It comes from someone with the power to enforce. 'She ordered them to change formation' — they MUST. No negotiation! 🔴"
        },

        {
          type: "mc",
          q: "Amara: 'Switch to a 4-4-2. NOW.' → She ___ the team ___ to a 4-4-2 immediately.",
          options: ["ordered / to switch", "asked / to switch", "reminded / to switch"],
          a: "ordered / to switch",
          tip: "No negotiation + authority = ordered. To switch = infinitive form.",
          successTip: "'NOW' = the urgency signal for ORDERED! No please, no discussion — she has the authority and the situation demands it. ASKED would feel wrong here! 🔴"
        },

        {
          type: "mc",
          q: "She ___ the captain ___ the defensive line higher — something she ___ only ever ___ before, never ___.",
          options: ["ordered / to push / had / asked / ordered", "asked / to push / had / ordered / asked", "warned / to push / had / asked / warned"],
          a: "ordered / to push / had / asked / ordered",
          tip: "Crisis moment = ordered. Before = only asked (polite). Now = ordered (authority). Had asked = Past Perfect.",
          successTip: "Timeline contrast! HAD ASKED (past — polite) vs ORDERED (now — authority). The shift from asking to ordering shows how the match situation changed her leadership approach! 🎭"
        },

        {
          type: "mc",
          q: "Choose the sentence that correctly captures the tone of ordering:",
          options: [
            "She ordered the striker to stop holding the ball and release it faster.",
            "She ordered the striker, please stop holding the ball.",
            "She ordered to the striker to stop holding the ball."
          ],
          a: "She ordered the striker to stop holding the ball and release it faster.",
          tip: "Ordered + person + to + infinitive. No 'please' (that's asking). No 'to the' (person goes directly after ordered).",
          successTip: "Error detection! 'Please' ❌ (please = asking, not ordering). 'To the striker' ❌ (person comes directly after ordered, no preposition). Option 1 = correct structure! 🔎"
        },

        {
          type: "mc",
          q: "At half-time, Amara had ___ calmly. By the 70th minute, she ___. The same player, the same instruction — but a completely different verb.",
          options: ["asked / ordered", "warned / asked", "reminded / warned"],
          a: "asked / ordered",
          tip: "Half-time = calm = asked. 70th minute = crisis authority = ordered. The match situation changes the verb.",
          successTip: "Same player, same instruction — different verb = different relationship! ASKED (half-time, calm) → ORDERED (70th minute, crisis). The verb tells you the score! ⚽🔴"
        },

        {
          type: "input",
          q: "Match report: 'With fifteen minutes left, Amara ___ the substitute ___ on immediately and ___ the midfielder ___ deeper.' (order — past simple, twice)",
          a: "ordered / to come / ordered / to drop",
          alts: ["Ordered / to come / ordered / to drop"],
          tip: "Two separate orders to two different players. Ordered + person + to + infinitive, twice."
        },

        {
          type: "mc",
          q: "Yuki noted: 'The moment Amara ___ rather than ___, you knew the match ___ at a crisis point.'",
          options: ["ordered / asked / was", "asked / ordered / had been", "warned / reminded / was"],
          a: "ordered / asked / was",
          tip: "The shift from asking to ordering = crisis signal. Was = backshift.",
          successTip: "ORDERED vs ASKED as a crisis indicator! Yuki understood that verb choice reveals situation. When a coach orders instead of asks, everything changes. Backshift: is → was! 📰⚽"
        },

        {
          type: "mc",
          q: "Amara ___ the goalkeeper ___ ___ the ball long — she ___ him ___ possession until the midfield ___ repositioned.",
          options: ["ordered / not to play / asked / to keep / had", "warned / not to play / reminded / to keep / was", "ordered / not to play / reminded / to hold / had"],
          a: "ordered / not to play / asked / to keep / had",
          tip: "Tactical command = ordered + not to. Positive tactical request = asked + to. Had repositioned = Past Perfect.",
          successTip: "ORDERED NOT TO PLAY (authority prohibition) + ASKED TO KEEP (polite tactical request) + HAD REPOSITIONED (Past Perfect — needed to happen first). Real tactical football! ⚽🔴"
        },

        {
          type: "order",
          q: "Reorder: 'Ella ordenó al capitán que cambiara la formación y que no permitiera que el equipo rival controlara el mediocampo.'",
          words: ["She", "ordered", "the", "captain", "to", "change", "the", "formation", "and", "not", "to", "let", "the", "opposing", "team", "control", "the", "midfield."],
          a: "She ordered the captain to change the formation and not to let the opposing team control the midfield.",
          extra: ["asked", "warned", "changing"]
        },

        {
          type: "input",
          q: "Post-match analysis: 'Amara had ___ politely for sixty minutes. Then she ___ — and the team responded immediately.' (ask / order — past perfect + past simple)",
          a: "asked / ordered",
          alts: ["Asked / ordered"],
          successTip: "HAD ASKED (past perfect — sixty minutes of politeness) then ORDERED (past simple — the moment of authority). The tense sequence tells the whole story of the match! 🏆⚽"
        }
      ]
    },

    // ===================================================================
    // SET 5: ENCOURAGED (B2-)
    // ===================================================================
    {
      id: "m10-u11-set5",
      title: "Set 5: Believe in Yourself",
      context: `*Accra, Friday — Yuki interviews Coach Amara*

Yuki had been following the team for three weeks.
She had noticed something about Amara's style:
she never just told players what to do — she also made them believe they could do it.
"The difference between a good coach and a great one," Amara said,
"is knowing when to order and when to encourage."

*Accra, viernes — Yuki entrevista a la entrenadora Amara*

Yuki llevaba tres semanas siguiendo al equipo.
Había notado algo sobre el estilo de Amara:
nunca solo decía a los jugadores qué hacer — también les hacía creer que podían hacerlo.
"La diferencia entre un buen entrenador y uno excelente," dijo Amara,
"es saber cuándo ordenar y cuándo animar."`,

      questions: [
        {
          type: "match",
          q: "Match 'encouraged' to its meaning and contrast:",
          pairs: [
            { left: "encouraged", right: "animó / alentó (apoyo emocional + acción)" },
            { left: "encouraged vs ordered", right: "Encouraged = motivated to try. Ordered = commanded to do." },
            { left: "encouraged vs asked", right: "Encouraged = builds confidence. Asked = makes a request." }
          ],
          a: "completed",
          successTip: "ENCOURAGED = you believe in the person AND you want them to act. It has both emotional support AND direction. 'She encouraged him to try' = I believe in you + please do this! 💛"
        },

        {
          type: "mc",
          q: "Amara saw that Léa was hesitating at the penalty spot during training. She ___ her ___ the shot with confidence.",
          options: ["encouraged / to take", "ordered / to take", "warned / to take"],
          a: "encouraged / to take",
          tip: "Hesitation + emotional support needed = encouraged. To take = infinitive.",
          successTip: "HESITATION = the key signal for ENCOURAGED! When someone doubts themselves, you don't ORDER — you ENCOURAGE! The emotional support is what makes this verb right! 💛"
        },

        {
          type: "mc",
          q: "Yuki wrote: 'Amara had always ___ young players ___ ___ their limits — but ___ them ___ ___ ___ if they felt pain.'",
          options: ["encouraged / to push / warned / not to continue", "asked / to push / reminded / not to", "encouraged / to exceed / ordered / to stop"],
          a: "encouraged / to push / warned / not to continue",
          tip: "Push limits = emotional encouragement. Danger signal = warning. Encouraged to push + warned not to continue.",
          successTip: "ENCOURAGED TO PUSH (positive motivation) + WARNED NOT TO CONTINUE (danger protection). Two verbs, two jobs — motivating AND protecting. That's great coaching! 💛⚠️"
        },

        {
          type: "mc",
          q: "Choose the sentence that uses 'encouraged' correctly:",
          options: [
            "She encouraged the young player to speak up in team meetings.",
            "She encouraged to the young player to speak up.",
            "She encouraged the young player speak up in team meetings."
          ],
          a: "She encouraged the young player to speak up in team meetings.",
          tip: "Encouraged + person + to + infinitive. No preposition before person. TO is required before infinitive.",
          successTip: "Error detection! 'Encouraged to the player' ❌ (no preposition between verb and person). 'Encouraged player speak' ❌ (missing TO before infinitive). Option 1 = perfect! 📌"
        },

        {
          type: "mc",
          q: "Amara ___ Léa ___ for the national team — something Léa ___ never ___ of doing before.",
          options: ["encouraged / to try out / had / thought", "ordered / to apply / had / dreamed", "asked / to train / has / thought"],
          a: "encouraged / to try out / had / thought",
          tip: "Motivating someone beyond their self-belief = encouraged. Had thought = Past Perfect (before Amara's encouragement).",
          successTip: "HAD NEVER THOUGHT = Past Perfect — before Amara's encouragement, Léa didn't believe it was possible. ENCOURAGED = I believe in you even when you don't believe in yourself! 💛"
        },

        {
          type: "input",
          q: "Training session: 'Every day Amara ___ the younger players ___ themselves — not just their bodies.' (encourage — past simple + to challenge)",
          a: "encouraged / to challenge",
          alts: ["Encouraged / to challenge"],
          tip: "Daily emotional support and direction = encouraged. To challenge themselves = infinitive."
        },

        {
          type: "mc",
          q: "Yuki ___ Marcus ___ the story of Amara's coaching philosophy — she ___ it ___ one of the most important sports stories of the year.",
          options: ["encouraged / to cover / believed / would be", "asked / to cover / believed / was", "persuaded / to cover / thought / had been"],
          a: "encouraged / to cover / believed / would be",
          tip: "Yuki believed in the story + urged Marcus to act = encouraged. Would be = backshift of will be.",
          successTip: "ENCOURAGED Marcus (not ordered, not just asked — she believed in the story AND wanted him to act). WOULD BE = will be backshifted. Encouragement isn't just for athletes! 💛"
        },

        {
          type: "mc",
          q: "Amara ___ each player individually during the week — ___ some ___ take on more responsibility, ___ others ___ trust their instincts more.",
          options: ["encouraged / encouraging / to / encouraging / to", "asked / asking / to / asking / to", "encouraged / encouraging / to / reminding / to"],
          a: "encouraged / encouraging / to / encouraging / to",
          tip: "Individual motivation for multiple players = all encouraged. To take + to trust = infinitives.",
          successTip: "ENCOURAGED across the whole squad — each person, individual encouragement, all with TO + infinitive. The consistency of ENCOURAGED shows her whole coaching philosophy! 💛⚽"
        },

        {
          type: "order",
          q: "Reorder: 'Ella animó a la joven delantera a intentarlo y a no tener miedo de fallar.'",
          words: ["She", "encouraged", "the", "young", "striker", "to", "try", "and", "not", "to", "be", "afraid", "of", "failing."],
          a: "She encouraged the young striker to try and not to be afraid of failing.",
          extra: ["ordered", "warned", "trying"]
        },

        {
          type: "input",
          q: "Yuki's profile: 'What set Amara apart was that she ___ players ___ beyond what they thought they ___ capable of.' (encourage — past simple + to go / be)",
          a: "encouraged / to go / were",
          alts: ["Encouraged / to go / were"],
          successTip: "ENCOURAGED TO GO beyond what they thought they WERE capable of — were = backshift of are. The Past Simple + infinitive + backshift in the embedded clause. Beautiful and complete! 💛🏆"
        }
      ]
    },

    // ===================================================================
    // SET 6: ADVISED (B2)
    // ===================================================================
    {
      id: "m10-u11-set6",
      title: "Set 6: Professional Guidance",
      context: `*Accra Sports Clinic, week four — Dr. Santos and Coach Amara meet*

Dr. Santos had been the team's physiotherapist for eight years.
He had outlasted four coaches. He had seen every type of injury and every type of mistake.
When Dr. Santos spoke, Amara listened — not because she had to,
but because his advice was always based on evidence.

*Clínica deportiva de Accra, semana cuatro — Dr. Santos y la entrenadora Amara se reúnen*

El Dr. Santos llevaba ocho años como fisioterapeuta del equipo.
Había sobrevivido a cuatro entrenadores. Había visto todo tipo de lesiones y de errores.
Cuando el Dr. Santos hablaba, Amara escuchaba — no porque tuviera que hacerlo,
sino porque sus consejos siempre estaban basados en la evidencia.`,

      questions: [
        {
          type: "match",
          q: "Match 'advised' to its meaning and contrast:",
          pairs: [
            { left: "advised", right: "aconsejó (consejo profesional basado en experiencia/conocimiento)" },
            { left: "advised vs warned", right: "Advised = professional recommendation. Warned = danger/consequence." },
            { left: "advised vs encouraged", right: "Advised = expert guidance. Encouraged = emotional support." }
          ],
          a: "completed",
          successTip: "ADVISED = expert giving informed professional guidance. A doctor advises. A lawyer advises. A physio advises. It's calm, reasoned, and based on knowledge — not emotion or authority! 💼"
        },

        {
          type: "mc",
          q: "Dr. Santos: 'You should rotate the squad more — the injury rate is directly linked to fatigue.' → He ___ Amara ___ the squad more.",
          options: ["advised / to rotate", "warned / to rotate", "encouraged / to rotate"],
          a: "advised / to rotate",
          tip: "Expert recommendation based on data = advised. To rotate = infinitive.",
          successTip: "'Directly linked to data' = professional evidence = ADVISED! Not WARNED (no immediate danger) not ENCOURAGED (not about belief). Expert guidance = ADVISED! 💼"
        },

        {
          type: "mc",
          q: "He ___ her ___ Léa ___ play in the next match — explaining that the scan ___ shown inflammation.",
          options: ["advised / not to let / had", "warned / not to let / had", "asked / not to let / has"],
          a: "advised / not to let / had",
          tip: "Professional medical recommendation = advised. Not to let = negative. Had shown = Past Perfect.",
          successTip: "ADVISED NOT TO LET = negative professional recommendation. Had shown = Past Perfect (the scan happened before the advice). Professional expertise + negative infinitive! 💼"
        },

        {
          type: "mc",
          q: "Choose the correct verb: 'Dr. Santos had ten years of data. He told Amara the training schedule was too intense.'",
          options: [
            "He advised Amara to reduce the training intensity.",
            "He warned Amara to reduce the training intensity.",
            "He encouraged Amara to reduce the training intensity."
          ],
          a: "He advised Amara to reduce the training intensity.",
          tip: "Ten years of data + professional recommendation = advised. No danger (not warned), no emotional support (not encouraged).",
          successTip: "Data + expertise + recommendation = ADVISED. WARNED needs a danger. ENCOURAGED needs an emotional element. Professional context with evidence = ADVISED! 💼"
        },

        {
          type: "mc",
          q: "Dr. Santos ___ both Amara and Léa ___ ___ the return-to-play protocol — adding that players who ___ it ___ a significantly higher re-injury rate.",
          options: ["advised / to follow / skipped / had", "warned / to follow / skip / had", "reminded / to follow / had skipped / have"],
          a: "advised / to follow / skipped / had",
          tip: "Protocol recommendation = advised. Skipped = past (backshifted from skip). Had = Past Perfect for re-injury data.",
          successTip: "ADVISED TO FOLLOW (professional guidance) + SKIPPED (backshift) + HAD (Past Perfect for the data he cited). Three layers in one professional recommendation! 💼⚕️"
        },

        {
          type: "input",
          q: "Clinic notes: 'Dr. Santos ___ the coach ___ training sessions by ten minutes each day and ___ any high-impact drills for two weeks.' (advise — past simple + shorten / avoid)",
          a: "advised / to shorten / to avoid",
          alts: ["Advised / to shorten / to avoid"],
          tip: "Two parallel professional recommendations. Advised + to shorten + to avoid. Both infinitives."
        },

        {
          type: "mc",
          q: "Amara later told Yuki: 'Dr. Santos ___ me ___ decisions based on the schedule rather than the players' physical condition. I ___ him I would think about it.'",
          options: ["advised / not to make / told", "warned / not to make / said", "asked / not to make / told"],
          a: "advised / not to make / told",
          tip: "Expert guidance negative = advised not to. Told him = TELL + person (direct report).",
          successTip: "ADVISED NOT TO MAKE (professional negative recommendation) + TOLD HIM (reported back to him — TELL + person). The advice came from expertise, not fear! 💼"
        },

        {
          type: "mc",
          q: "Marcus wrote: 'The physio ___ the coaching staff ___ the pre-season programme, ___ that the current schedule ___ leading to preventable injuries.'",
          options: ["advised / to review / arguing / was", "warned / to change / claiming / had been", "asked / to look at / explaining / is"],
          a: "advised / to review / arguing / was",
          tip: "Professional recommendation = advised. Logical reasoning given = arguing. Current schedule leading to injuries = was (backshift).",
          successTip: "ADVISED TO REVIEW (professional guidance) + ARGUING (logical reasoning from Unit 10!) + WAS (backshift). Two units' verbs in one sentence — expert advice + logical argument! 💼🎓"
        },

        {
          type: "order",
          q: "Reorder: 'El médico aconsejó al entrenador que redujera la carga de entrenamiento y que no permitiera que los jugadores lesionados volvieran antes de tiempo.'",
          words: ["The", "doctor", "advised", "the", "coach", "to", "reduce", "the", "training", "load", "and", "not", "to", "let", "injured", "players", "return", "early."],
          a: "The doctor advised the coach to reduce the training load and not to let injured players return early.",
          extra: ["warned", "ordered", "reducing"]
        },

        {
          type: "input",
          q: "Season medical report: 'Dr. Santos had ___ the entire coaching staff ___ player welfare over results — and the statistics showed he ___ right.' (advise + past perfect + prioritise / be)",
          a: "advised / to prioritise / was",
          alts: ["Advised / to prioritise / was"],
          successTip: "HAD ADVISED TO PRIORITISE (Past Perfect — advice given before the season) + WAS RIGHT (backshifted is — confirmed by statistics). Expert advice vindicated by data! 💼✅"
        }
      ]
    },

    // ===================================================================
    // SET 7: PERSUADED (B2)
    // ===================================================================
    {
      id: "m10-u11-set7",
      title: "Set 7: Changing Minds",
      context: `*Accra press room, week five — Marcus and Coach Amara*

Marcus had been trying to get an interview with Amara for three weeks.
She never gave interviews. She didn't trust journalists — "they always get it wrong," she said.
But Marcus was patient, persistent, and very good at his job.
By the end of the week, he had persuaded her.

*Sala de prensa de Accra, semana cinco — Marcus y la entrenadora Amara*

Marcus llevaba tres semanas intentando conseguir una entrevista con Amara.
Ella nunca daba entrevistas. No confiaba en los periodistas — "siempre se equivocan," decía.
Pero Marcus era paciente, persistente y muy bueno en su trabajo.
Al final de la semana, la había convencido.`,

      questions: [
        {
          type: "match",
          q: "Match 'persuaded' to its meaning and contrast:",
          pairs: [
            { left: "persuaded", right: "convenció (no quería hacerlo — pero lo hizo)" },
            { left: "persuaded vs encouraged", right: "Persuaded = changed their mind. Encouraged = supported existing desire." },
            { left: "persuaded vs asked", right: "Persuaded = they resisted, then agreed. Asked = simple request." }
          ],
          a: "completed",
          successTip: "PERSUADED = they didn't want to — but you convinced them and they DID it! It implies resistance overcome. 'She persuaded him to stay' = he wanted to leave, but she changed his mind! 🔄"
        },

        {
          type: "mc",
          q: "Marcus: 'Please — just one hour. I'll show you the draft before publishing.' → He ___ Amara ___ him one hour for an interview.",
          options: ["persuaded / to give", "asked / to give", "encouraged / to give"],
          a: "persuaded / to give",
          tip: "She resisted for three weeks, then agreed = persuaded. Result achieved after resistance.",
          successTip: "Three weeks of resistance then agreement = PERSUADED! ASKED would be too neutral — it doesn't capture the resistance. PERSUADED = the resistance was overcome! 🔄"
        },

        {
          type: "mc",
          q: "She ___ never ___ the interview to anyone who ___ ___ her before — but Marcus ___ her.",
          options: ["had / given / had / asked / persuaded", "has / given / had / persuaded / asked", "would / give / has / warned / persuaded"],
          a: "had / given / had / asked / persuaded",
          tip: "Had given = Past Perfect (never before). Had asked = Past Perfect (previous journalists). Persuaded = Past Simple (Marcus achieved it). Three tenses, one story!",
          successTip: "HAD GIVEN (never before — Past Perfect) + HAD ASKED (others tried — Past Perfect) + PERSUADED (Marcus succeeded — Past Simple). The timeline of resistance and breakthrough! 🔄🏆"
        },

        {
          type: "mc",
          q: "Choose the sentence where 'persuaded' is used correctly:",
          options: [
            "Marcus persuaded Amara to speak publicly about her coaching philosophy.",
            "Marcus persuaded to Amara to speak publicly.",
            "Marcus persuaded Amara speaking publicly about her coaching philosophy."
          ],
          a: "Marcus persuaded Amara to speak publicly about her coaching philosophy.",
          tip: "Persuaded + person + to + infinitive. No preposition before person. TO required before infinitive.",
          successTip: "Error detection! 'Persuaded to Amara' ❌ (no preposition). 'Persuaded Amara speaking' ❌ (gerund — TO is required). Option 1 = correct! Persuaded + person + TO + infinitive! 📌"
        },

        {
          type: "mc",
          q: "Yuki had also ___ Marcus ___ the story earlier — but it was Marcus who eventually ___ Amara ___ her story.",
          options: ["encouraged / to pursue / persuaded / to tell", "asked / to pursue / reminded / to tell", "persuaded / to pursue / encouraged / to tell"],
          a: "encouraged / to pursue / persuaded / to tell",
          tip: "Yuki believed in the story + wanted Marcus to act = encouraged. Amara resisted + then agreed = persuaded.",
          successTip: "ENCOURAGED (Yuki → Marcus, believing in the story) vs PERSUADED (Marcus → Amara, overcoming her resistance). Two different verbs, two different relationships! 💛🔄"
        },

        {
          type: "input",
          q: "Marcus's diary: 'It took me three weeks, but I finally ___ her ___ on record about her coaching methods.' (persuade — past simple + speak)",
          a: "persuaded / to speak",
          alts: ["Persuaded / to speak"],
          tip: "Three weeks + finally = resistance overcome = persuaded. To speak = infinitive."
        },

        {
          type: "mc",
          q: "During the interview, Amara ___ Marcus ___ ___ the parts about her personal life — he ___ her that those sections ___ the most important.",
          options: ["asked / to remove / persuaded / were", "warned / to remove / persuaded / had been", "ordered / not to print / told / were"],
          a: "asked / to remove / persuaded / were",
          tip: "Polite request to remove = asked. Marcus changed her mind = persuaded. Those sections were important = were (backshift).",
          successTip: "ASKED (she requested removal) → PERSUADED (Marcus convinced her otherwise) — even within the interview, the dynamic of requesting and persuading continued! Were = backshift! 🔄"
        },

        {
          type: "mc",
          q: "Marcus later told Priya: 'I ___ her ___ the draft — and once she ___ it, she ___ me ___ everything I ___ originally planned.'",
          options: ["persuaded / to read / had read / let / to include / had", "asked / to read / read / persuaded / include / had", "encouraged / to see / saw / asked / to keep / has"],
          a: "persuaded / to read / had read / let / to include / had",
          tip: "Convinced her to read = persuaded. Once she read = had read (Past Perfect). Then she let him include = let (Past Simple). Originally planned = had planned (Past Perfect).",
          successTip: "The full persuasion story in one sentence! PERSUADED (convinced) + HAD READ (Past Perfect — before her decision) + LET (Past Simple — her response) + HAD PLANNED (original plan). Epic timeline! 🏆"
        },

        {
          type: "order",
          q: "Reorder: 'Él la convenció de que contara su historia, aunque ella había rechazado todas las solicitudes anteriores.'",
          words: ["He", "persuaded", "her", "to", "tell", "her", "story,", "even", "though", "she", "had", "rejected", "all", "previous", "requests."],
          a: "He persuaded her to tell her story, even though she had rejected all previous requests.",
          extra: ["asked", "had", "telling"]
        },

        {
          type: "input",
          q: "Priya's review: 'The fact that Marcus ___ Amara ___ at all was remarkable — she ___ never ___ a journalist into her world before.' (persuade + past simple / let + past perfect)",
          a: "persuaded / to talk / had / let",
          alts: ["Persuaded / to talk / had / let"],
          successTip: "PERSUADED TO TALK (resistance overcome) + HAD LET (Past Perfect — never before). The contrast between never before and now captures exactly what PERSUADED means! 🔄✅"
        }
      ]
    },

    // ===================================================================
    // SET 8: WARNED vs ADVISED — DIRECT CONTRAST (B2)
    // ===================================================================
    {
      id: "m10-u11-set8",
      title: "Set 8: Danger vs Guidance",
      context: `*Accra Sports Clinic, week six — the biggest injury scare of the season*

Léa had played through pain for two matches.
Dr. Santos had noticed — and now he had confirmation from the scans.
He needed to speak to both Léa and Coach Amara.
"What I say to each of them will be different," he told his assistant.
"One needs a warning. The other needs advice."

*Clínica deportiva de Accra, semana seis — el mayor susto por lesión de la temporada*

Léa había jugado con dolor durante dos partidos.
El Dr. Santos se había dado cuenta — y ahora tenía la confirmación de los escáneres.
Necesitaba hablar tanto con Léa como con la entrenadora Amara.
"Lo que le diga a cada una será diferente," le dijo a su asistente.
"Una necesita una advertencia. La otra necesita un consejo."`,

      questions: [
        {
          type: "match",
          q: "Match the contrast between warned and advised:",
          pairs: [
            { left: "warned", right: "Peligro inmediato — consecuencia grave si no se cumple." },
            { left: "advised", right: "Recomendación profesional — mejor curso de acción basado en experiencia." },
            { left: "key difference", right: "Warned = urgency + risk. Advised = expertise + guidance." }
          ],
          a: "completed",
          successTip: "WARNED = stop, danger, consequence. ADVISED = here's what the expert recommends. 'He warned her not to run' (danger). 'He advised her to rest' (professional guidance). Both helpful — different weight! ⚠️💼"
        },

        {
          type: "mc",
          q: "To Léa: 'If you play on Saturday, you risk ending your career.' → He ___ Léa ___ ___ on Saturday.",
          options: ["warned / not to play", "advised / not to play", "asked / not to play"],
          a: "warned / not to play",
          tip: "Career-ending risk = serious danger = warned. Not to play = negative infinitive.",
          successTip: "'Risk ending your career' = maximum danger signal = WARNED! ADVISED would be too gentle here. When careers are at stake, the verb is WARNED! ⚠️"
        },

        {
          type: "mc",
          q: "To Amara: 'Based on the scan data, you should restructure the training schedule for the next four weeks.' → He ___ Amara ___ the training schedule for the next four weeks.",
          options: ["advised / to restructure", "warned / to restructure", "encouraged / to restructure"],
          a: "advised / to restructure",
          tip: "Based on data + professional recommendation = advised. No immediate danger to Amara.",
          successTip: "'Based on scan data' = professional expertise = ADVISED! Amara isn't in danger — she needs expert guidance. WARNED would imply SHE was at risk, which isn't the case! 💼"
        },

        {
          type: "mc",
          q: "Choose the correct verb for each sentence: 'He ___ Léa ___ train. He ___ Amara ___ the workload.'",
          options: ["warned / not to / advised / to reduce", "advised / not to / warned / to reduce", "warned / not to / warned / to reduce"],
          a: "warned / not to / advised / to reduce",
          tip: "Léa = danger to her body = warned. Amara = professional scheduling recommendation = advised.",
          successTip: "Perfect contrast! WARNED Léa (danger to her body) + ADVISED Amara (professional scheduling recommendation). Same doctor, same meeting, two different verbs for two different messages! ⚠️💼"
        },

        {
          type: "mc",
          q: "Dr. Santos had ___ Léa ___ the pain months ago — and ___ her ___ to a specialist immediately.",
          options: ["warned / not to ignore / advised / to go", "advised / not to ignore / warned / to go", "reminded / not to ignore / asked / to go"],
          a: "warned / not to ignore / advised / to go",
          tip: "Ignoring pain = danger = warned. Going to specialist = professional recommendation = advised.",
          successTip: "WARNED NOT TO IGNORE (danger — ignoring pain has consequences) + ADVISED TO GO (professional guidance — see a specialist). Two messages, two verbs, both correct! ⚠️💼"
        },

        {
          type: "input",
          q: "Clinic summary: 'Dr. Santos ___ Léa ___ ___ training entirely and ___ Amara ___ an additional rest day into the weekly programme.' (warn + stop / advise + build — past simple)",
          a: "warned / to stop / advised / to build",
          alts: ["Warned / to stop / advised / to build"],
          tip: "Léa must stop = warning. Amara should build in rest = professional advice."
        },

        {
          type: "mc",
          q: "Marcus's article: 'The physio ___ the player she ___ through any more pain — and ___ the coaching staff ___ the intensity of training until the issue ___ resolved.'",
          options: ["warned / not to push / advised / to reduce / was", "advised / not to push / warned / to change / had been", "warned / to stop / asked / to reduce / is"],
          a: "warned / not to push / advised / to reduce / was",
          tip: "Player danger = warned not to push. Staff professional guidance = advised to reduce. Was = backshift of is.",
          successTip: "WARNED (player, danger) + ADVISED (staff, professional) + WAS (backshift). The same injury situation required two completely different verbs for two different people! ⚠️💼"
        },

        {
          type: "mc",
          q: "After the season, Dr. Santos reflected: 'I ___ players ___ their limits for twenty years. But I also ___ them ___ ___ when they needed to. Those two things together — that's sports medicine.'",
          options: ["advised / to respect / warned / not to continue", "warned / to respect / advised / not to continue", "encouraged / to push / warned / not to"],
          a: "advised / to respect / warned / not to continue",
          tip: "Professional limit-setting = advised. Stopping when danger = warned. Both verbs together = complete picture.",
          successTip: "ADVISED TO RESPECT LIMITS (professional guidance) + WARNED NOT TO CONTINUE (danger signal). His whole career in two verbs! Sports medicine in one sentence! 💼⚠️🏆"
        },

        {
          type: "order",
          q: "Reorder: 'El médico advirtió a la jugadora que no jugara y aconsejó al entrenador que redujera la intensidad de los entrenamientos durante un mes.'",
          words: ["The", "doctor", "warned", "the", "player", "not", "to", "play", "and", "advised", "the", "coach", "to", "reduce", "training", "intensity", "for", "a", "month."],
          a: "The doctor warned the player not to play and advised the coach to reduce training intensity for a month.",
          extra: ["asked", "encouraged", "playing"]
        },

        {
          type: "input",
          q: "Priya's column: 'There is a difference between ___ someone ___ something and ___ them ___ something better. One is a warning. The other is wisdom.' (warn / not to do — advise / to do)",
          a: "warning / not to do / advising / to do",
          alts: ["Warning / not to do / advising / to do"],
          successTip: "WARNING NOT TO DO (danger) vs ADVISING TO DO (wisdom). The contrast in its purest form — one stops harm, the other creates good. Both essential! ⚠️💼✅"
        }
      ]
    },

    // ===================================================================
    // SET 9: ENCOURAGED vs PERSUADED — CONTRAST (B2+)
    // ===================================================================
    {
      id: "m10-u11-set9",
      title: "Set 9: Wanting To vs Not Wanting To",
      context: `*Accra, semi-final week — two very different conversations*

Coach Amara needed two things before the semi-final:
Léa back on the pitch — and a quiet midfielder called Kwame to step up as captain.
The problem was that Léa was afraid of re-injury.
And Kwame had never wanted to be a leader.
Two players. Two conversations. Two completely different verbs.

*Accra, semana de semifinal — dos conversaciones muy diferentes*

La entrenadora Amara necesitaba dos cosas antes de la semifinal:
a Léa de vuelta en el campo — y a un centrocampista tranquilo llamado Kwame que asumiera el liderazgo.
El problema era que Léa tenía miedo de volver a lesionarse.
Y Kwame nunca había querido ser líder.
Dos jugadores. Dos conversaciones. Dos verbos completamente diferentes.`,

      questions: [
        {
          type: "match",
          q: "Match the contrast between encouraged and persuaded:",
          pairs: [
            { left: "encouraged", right: "They wanted to but lacked confidence — you supported them." },
            { left: "persuaded", right: "They didn't want to at all — you changed their mind." },
            { left: "key question", right: "Did they WANT to but doubt themselves? → encouraged. Did they NOT WANT to? → persuaded." }
          ],
          a: "completed",
          successTip: "The crucial distinction! ENCOURAGED = they wanted it but needed belief. PERSUADED = they didn't want it but you changed their mind. The person's starting position is everything! 💛🔄"
        },

        {
          type: "mc",
          q: "Léa wanted to return but was scared of her knee. Amara ___ her ___ back — slowly, carefully, on her own terms.",
          options: ["encouraged / to come", "persuaded / to come", "asked / to come"],
          a: "encouraged / to come",
          tip: "Léa WANTED to return but lacked confidence = encouraged. Not persuaded (she wasn't refusing).",
          successTip: "Léa WANTED to play — she was just scared. ENCOURAGED = I believe in you, you can do this! PERSUADED would be wrong — she wasn't refusing, she was doubting! 💛"
        },

        {
          type: "mc",
          q: "Kwame had never seen himself as a leader and had already said no when asked. Amara ___ him ___ the captain's armband for the semi-final.",
          options: ["persuaded / to wear", "encouraged / to wear", "reminded / to wear"],
          a: "persuaded / to wear",
          tip: "Had said no = active resistance = persuaded (mind changed). Not just lacking confidence.",
          successTip: "'Had already said no' = active refusal = PERSUADED! He didn't just doubt himself — he actively didn't want it. Amara changed his mind. That's PERSUADED! 🔄"
        },

        {
          type: "mc",
          q: "'I don't want to be captain.' said Kwame. → She ___ him ___ himself as more than a midfielder.",
          options: ["persuaded / to see", "encouraged / to see", "asked / to see"],
          a: "persuaded / to see",
          tip: "Active resistance ('I don't want') = persuaded. Seeing himself differently = the change achieved.",
          successTip: "'I don't want' = resistance to overcome = PERSUADED. If he had said 'I'm not sure I can' (doubt, not refusal), ENCOURAGED would be correct! The difference matters! 🔄"
        },

        {
          type: "mc",
          q: "With Léa, Amara ___ her ___ ___ herself — not to perform. With Kwame, she ___ him ___ a role he ___ never wanted.",
          options: ["encouraged / to trust / persuaded / to accept / had", "persuaded / to trust / encouraged / to accept / had", "asked / to believe / persuaded / to take / has"],
          a: "encouraged / to trust / persuaded / to accept / had",
          tip: "Léa = confidence issue = encouraged. Kwame = resistance = persuaded. Had never wanted = Past Perfect.",
          successTip: "ENCOURAGED (Léa — wanted but doubted) vs PERSUADED (Kwame — didn't want at all). Had never wanted = Past Perfect (before Amara's intervention). Perfect contrast! 💛🔄"
        },

        {
          type: "input",
          q: "Yuki's match preview: 'Amara had ___ Léa ___ her instincts again and ___ Kwame ___ the most important role of his career.' (encourage + trust / persuade + accept — past perfect)",
          a: "encouraged / to trust / persuaded / to accept",
          alts: ["Encouraged / to trust / persuaded / to accept"],
          tip: "Past Perfect = before the match. Encouraged to trust + persuaded to accept."
        },

        {
          type: "mc",
          q: "Marcus wrote: 'It is one thing ___ a player ___ believe in themselves. It is another entirely ___ a player ___ become something they never thought they ___ be.'",
          options: ["to encourage / to / to persuade / to / would", "to persuade / to / to encourage / to / could", "encouraging / to / persuading / to / should"],
          a: "to encourage / to / to persuade / to / would",
          tip: "General truths: to encourage (confidence), to persuade (change of mind). Would = backshift of will for embedded belief.",
          successTip: "Infinitive form for general truths! TO ENCOURAGE (build confidence) vs TO PERSUADE (change minds). WOULD = backshift of 'they would never be'. The contrast as a universal truth! 💛🔄"
        },

        {
          type: "mc",
          q: "Before the match, Amara ___ both players: she ___ Léa ___ the noise and ___ Kwame ___ the team. Each verb was exact.",
          options: ["spoke to / encouraged / to ignore / persuaded / to lead", "met / warned / to ignore / asked / to lead", "talked to / reminded / to forget / encouraged / to lead"],
          a: "spoke to / encouraged / to ignore / persuaded / to lead",
          tip: "Léa = confidence support = encouraged to ignore. Kwame = resistance overcome = persuaded to lead.",
          successTip: "ENCOURAGED TO IGNORE (the noise — confidence support for someone who wants to play) + PERSUADED TO LEAD (resistance overcome for someone who didn't want to). Final precision! 💛🔄"
        },

        {
          type: "order",
          q: "Reorder: 'Ella animó a la jugadora lesionada a confiar en sí misma y convenció al centrocampista de asumir el liderazgo.'",
          words: ["She", "encouraged", "the", "injured", "player", "to", "trust", "herself", "and", "persuaded", "the", "midfielder", "to", "take", "on", "the", "leadership."],
          a: "She encouraged the injured player to trust herself and persuaded the midfielder to take on the leadership.",
          extra: ["asked", "warned", "trusting"]
        },

        {
          type: "input",
          q: "Post-match: 'Léa ___ and scored. Kwame ___ and captained brilliantly — despite never ___ ___ to do so.' (come back / lead / want — past simple + past perfect negative)",
          a: "came back / led / having wanted",
          alts: ["Came back / led / having wanted"],
          successTip: "CAME BACK (encouraged) + LED (persuaded) + HAVING WANTED (perfect participle — never wanted to before). The results of two different verbs, two different conversations! 💛🔄🏆"
        }
      ]
    },

    // ===================================================================
    // SET 10: ALL VERBS — FULL NARRATIVE (B2+)
    // ===================================================================
    {
      id: "m10-u11-set10",
      title: "Set 10: The Season in Reported Speech",
      context: `*Accra, final day of the season — the trophy presentation*

The team had won. Not because of luck — because of how people had been spoken to.
Asked to try. Reminded to focus. Warned to be careful.
Ordered at the right moments. Encouraged to believe.
Advised by expertise. Persuaded to step beyond themselves.
Every verb had mattered. Every word had done its job.

*Accra, último día de la temporada — la entrega del trofeo*

El equipo había ganado. No por suerte — sino por cómo se había hablado a las personas.
Se les pidió que lo intentaran. Se les recordó que se concentraran. Se les advirtió que tuvieran cuidado.
Se les ordenó en los momentos correctos. Se les animó a creer.
Se les aconsejó con experiencia. Se les convenció de ir más allá de sí mismos.
Cada verbo había importado. Cada palabra había hecho su trabajo.`,

      questions: [
        {
          type: "mc",
          q: "Match each verb to the right context from the season: 'Here is what I think you should do' / 'You MUST do this' / 'I believe in you — try it'",
          options: [
            "advised / ordered / encouraged",
            "warned / asked / persuaded",
            "reminded / ordered / encouraged"
          ],
          a: "advised / ordered / encouraged",
          tip: "Professional recommendation = advised. Non-negotiable command = ordered. Emotional support + action = encouraged.",
          successTip: "Three verbs, three contexts: ADVISED (expert guidance), ORDERED (authority command), ENCOURAGED (emotional support). Each one has a different relationship dynamic! 💼🔴💛"
        },

        {
          type: "mc",
          q: "Yuki wrote: 'Amara ___ her players ___ early, ___ them ___ the basics, ___ the captain ___ change formation — all in the same match.'",
          options: ["asked / to arrive / reminded / to trust / ordered / to", "warned / to arrive / reminded / to trust / asked / to", "asked / to arrive / told / to trust / ordered / to"],
          a: "asked / to arrive / reminded / to trust / ordered / to",
          tip: "Polite request = asked. Prior knowledge nudge = reminded. Crisis command = ordered.",
          successTip: "ASKED (polite pre-match) + REMINDED (reinforcing knowledge) + ORDERED (match crisis). Three verbs, three moments, three different coaching registers! 🎭⚽"
        },

        {
          type: "mc",
          q: "Dr. Santos ___ Léa ___ ___ too fast and ___ Amara ___ the squad's recovery programme. Both listened — both verbs were right.",
          options: ["warned / not to return / advised / to extend", "advised / not to return / warned / to extend", "asked / not to return / reminded / to follow"],
          a: "warned / not to return / advised / to extend",
          tip: "Léa = danger = warned. Amara = professional programme guidance = advised.",
          successTip: "WARNED (Léa, danger) + ADVISED (Amara, professional). The doctor used the right verb for the right person. The verbs show he understood the difference! ⚠️💼"
        },

        {
          type: "mc",
          q: "Marcus ___ Amara ___ the story three times before she agreed. The final article was one she ___ never ___ if he ___ asked rather than ___.",
          options: ["persuaded / to tell / would have / given / had / persuaded", "asked / to tell / would / given / hadn't / asked", "convinced / telling / had / given / would / persuaded"],
          a: "persuaded / to tell / would have / given / had / persuaded",
          tip: "Three weeks resistance + final agreement = persuaded. Would have given = third conditional. Had only asked = Past Perfect conditional.",
          successTip: "PERSUADED (resistance overcome) + WOULD HAVE GIVEN (third conditional — hypothetical past) + HAD ASKED (Past Perfect in conditional). Elite grammar, one sentence! 🔄🏆"
        },

        {
          type: "mc",
          q: "Kwame later told Yuki: 'Amara ___ me ___ the captaincy. I ___ her it was impossible. She ___ me ___ it anyway. Now I understand why.'",
          options: ["asked / to take / told / persuaded / to try", "persuaded / to accept / told / encouraged / to believe", "ordered / to take / said / persuaded / to do"],
          a: "asked / to take / told / persuaded / to try",
          tip: "First request = asked. His response (told her) = told + person. Changed his mind = persuaded. To try = infinitive.",
          successTip: "ASKED (first polite request) + TOLD (Kwame's response — TELL + person) + PERSUADED (she changed his mind). The three-verb arc of his transformation! 💛🔄"
        },

        {
          type: "mc",
          q: "Léa said: 'Dr. Santos ___ me ___ ___ too soon. Amara ___ me ___ myself again. Between them, they ___ me ___ the player I am now.'",
          options: ["warned / not to return / encouraged / to trust / helped / become", "advised / not to play / persuaded / to trust / asked / to be", "reminded / not to rush / encouraged / to believe / told / to become"],
          a: "warned / not to return / encouraged / to trust / helped / become",
          tip: "Medical danger = warned. Confidence building = encouraged. General causation = helped + bare infinitive.",
          successTip: "WARNED (medical danger) + ENCOURAGED (confidence) + HELPED BECOME (helped + bare infinitive, no 'to'). Three people, three verbs, one player's transformation! ⚠️💛🏆"
        },

        {
          type: "mc",
          q: "At the trophy ceremony, Amara said: 'I never ___ anyone ___ do anything they truly ___ ___ ___ do — I just ___ them that they ___.'",
          options: ["ordered / to / couldn't / could / told / could", "asked / to / didn't want / persuaded / showed / could", "made / to / refused / helped / reminded / should"],
          a: "ordered / to / couldn't / could / told / could",
          tip: "Her philosophy: never ordered what they couldn't do — just told them they could. Couldn't/could = backshift of can't/can.",
          successTip: "NEVER ORDERED (her principle) + COULDN'T DO (their self-belief) + TOLD THEM THEY COULD (her message). Could = backshift of can. Her whole philosophy in reported speech! 🏆"
        },

        {
          type: "order",
          q: "Reorder: 'Les pidió que lo intentaran, les recordó lo que sabían, les advirtió del peligro, los animó a creer y convenció a los que dudaban.'",
          words: ["She", "asked", "them", "to", "try,", "reminded", "them", "what", "they", "knew,", "warned", "them", "of", "the", "danger,", "encouraged", "them", "to", "believe,", "and", "persuaded", "those", "who", "doubted."],
          a: "She asked them to try, reminded them what they knew, warned them of the danger, encouraged them to believe, and persuaded those who doubted.",
          extra: ["ordered", "advised", "telling"]
        },

        {
          type: "mc",
          q: "Priya's final piece used all seven verbs. Which sequence correctly describes the season's arc?",
          options: [
            "Asked (politely) → Reminded (reinforced) → Warned (protected) → Ordered (commanded) → Encouraged (motivated) → Advised (guided) → Persuaded (transformed).",
            "Ordered → Warned → Asked → Reminded → Persuaded → Encouraged → Advised.",
            "Persuaded → Ordered → Reminded → Advised → Warned → Asked → Encouraged."
          ],
          a: "Asked (politely) → Reminded (reinforced) → Warned (protected) → Ordered (commanded) → Encouraged (motivated) → Advised (guided) → Persuaded (transformed).",
          tip: "The arc of a season: gentle to firm, protective to transformative. Each verb has its moment.",
          successTip: "The perfect arc! ASKED (gentle start) through PERSUADED (full transformation). Seven verbs, one season, one complete leadership story. That's the whole unit! 🏆⚽"
        },

        {
          type: "input",
          q: "Trophy speech summary: 'She ___ them ___ early, ___ them ___ focused, ___ them ___ push too hard, and ___ the ones who doubted ___ past their own limits.' (ask/arrive — remind/stay — warn/not to — persuade/go — past simple)",
          a: "asked / to arrive / reminded / to stay / warned / not to / persuaded / to go",
          alts: ["Asked / to arrive / reminded / to stay / warned / not to / persuaded / to go"],
          successTip: "🏆 UNIT 11 COMPLETE! All seven verbs mastered ✅ Positive and negative infinitives ✅ Object placement ✅ Backshift in context ✅ Semantic contrast between all verbs ✅ You tell people what to do — and how you say it matters! 🌍⚽"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u11-reading",
    title: "Reading: The Season in Words",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**Day One**

Coach Amara asked the players to arrive fifteen minutes early. She reminded them not to use phones during meetings and warned the squad not to underestimate the season ahead. She did not order anything — not yet.`
      },
      {
        type: "mc",
        q: "What did Amara ask the players to do?",
        options: ["Arrive fifteen minutes early", "Put away their phones", "Train harder"],
        a: "Arrive fifteen minutes early",
        tip: "'She asked the players to arrive fifteen minutes early.'"
      },
      {
        type: "mc",
        q: "What did she warn the squad about?",
        options: ["Not to underestimate the season", "Not to use phones", "Not to arrive late"],
        a: "Not to underestimate the season",
        tip: "'...warned the squad not to underestimate the season ahead.'"
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**The Injury**

Dr. Santos warned Léa not to train on the injured muscle and advised Coach Amara to reduce the squad's workload. Léa asked him how long she would be out. He advised her to wait for the scan results before making any decisions.`
      },
      {
        type: "mc",
        q: "What did Dr. Santos warn Léa not to do?",
        options: ["Train on the injured muscle", "Play in the next match", "Ignore the pain"],
        a: "Train on the injured muscle",
        tip: "'Dr. Santos warned Léa not to train on the injured muscle.'"
      },
      {
        type: "mc",
        q: "What did he advise Léa to do?",
        options: ["Wait for the scan results", "Rest for two weeks", "See a specialist"],
        a: "Wait for the scan results",
        tip: "'He advised her to wait for the scan results before making any decisions.'"
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**The Turning Point**

With twenty minutes left and the team losing, Amara ordered the captain to change formation. She also encouraged Léa to trust her instincts and reminded the defenders not to push forward. The team equalised within seven minutes.`
      },
      {
        type: "mc",
        q: "What did Amara order the captain to do?",
        options: ["Change the formation", "Push forward", "Speak to the team"],
        a: "Change the formation",
        tip: "'Amara ordered the captain to change formation.'"
      },
      {
        type: "mc",
        q: "What did she encourage Léa to do?",
        options: ["Trust her instincts", "Push forward", "Take the penalty"],
        a: "Trust her instincts",
        tip: "'She also encouraged Léa to trust her instincts.'"
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**The Captain**

Kwame had said no when first asked. But Amara persuaded him to wear the captain's armband for the semi-final. She told him she had always seen leadership in him, even when he hadn't seen it himself. He agreed to try.`
      },
      {
        type: "mc",
        q: "What did Amara persuade Kwame to do?",
        options: ["Wear the captain's armband", "Play in a new position", "Speak to the press"],
        a: "Wear the captain's armband",
        tip: "'Amara persuaded him to wear the captain's armband.'"
      },
      {
        type: "mc",
        q: "What did Kwame agree to do?",
        options: ["Try", "Train harder", "Give an interview"],
        a: "Try",
        tip: "'He agreed to try.'"
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**After the Trophy**

Marcus asked Amara to describe her coaching philosophy. She said she had never ordered what she could ask for, and had always encouraged players to go beyond what they believed they could do. She added that the trophy belonged to the words, not the tactics.`
      },
      {
        type: "mc",
        q: "What was Amara's philosophy about ordering?",
        options: ["She never ordered what she could ask for", "She only ordered in the final minutes", "She preferred ordering to encouraging"],
        a: "She never ordered what she could ask for",
        tip: "'She said she had never ordered what she could ask for.'"
      },
      {
        type: "mc",
        q: "What did she say the trophy belonged to?",
        options: ["The words, not the tactics", "The players, not the coach", "The preparation, not the match"],
        a: "The words, not the tactics",
        tip: "'She added that the trophy belonged to the words, not the tactics.'",
        successTip: "All seven reporting verbs from the unit appeared across these five passages — each doing exactly the right job. Unit 11 complete! 🏆⚽🌍"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u11-review",
    title: "Unit 11 Review: Reporting Verbs + Object + Infinitive",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: She ___ the players ___ early — it was a polite request, not a command.",
        options: ["asked / to arrive", "ordered / to arrive", "warned / to arrive"],
        a: "asked / to arrive",
        tip: "Polite request = asked + person + to + infinitive."
      },
           {
        type: "mc",
        q: "Review: He ___ her ___ on the injured leg — there was a real risk of permanent damage.",
        options: ["warned / not to train", "advised / not to train", "reminded / not to train"],
        a: "warned / not to train",
        tip: "Permanent damage risk = danger = warned + not to + infinitive."
      },
      {
        type: "mc",
        q: "Review: She ___ them ___ the warm-up protocol — they had learned it weeks ago.",
        options: ["reminded / to follow", "asked / to follow", "warned / to follow"],
        a: "reminded / to follow",
        tip: "Already knew it = reminded. Prior knowledge + nudge = reminded."
      },
      {
        type: "mc",
        q: "Review: With the team losing, she ___ the captain ___ the formation immediately.",
        options: ["ordered / to change", "asked / to change", "encouraged / to change"],
        a: "ordered / to change",
        tip: "Crisis + authority + no option to refuse = ordered."
      },
      {
        type: "mc",
        q: "Review: He ___ the young player ___ for the national team — she had the talent but not the confidence.",
        options: ["encouraged / to try out", "persuaded / to try out", "ordered / to try out"],
        a: "encouraged / to try out",
        tip: "Had talent but lacked confidence = wanted to but doubted herself = encouraged."
      },
      {
        type: "mc",
        q: "Review: Dr. Santos ___ Amara ___ the training load based on ten years of injury data.",
        options: ["advised / to reduce", "warned / to reduce", "encouraged / to reduce"],
        a: "advised / to reduce",
        tip: "Expert recommendation based on data = advised + person + to + infinitive."
      },
      {
        type: "mc",
        q: "Review: Marcus ___ Amara ___ the interview after three weeks of refusals.",
        options: ["persuaded / to give", "encouraged / to give", "asked / to give"],
        a: "persuaded / to give",
        tip: "Active resistance overcome = persuaded. She didn't want to — then she did."
      },
      {
        type: "mc",
        q: "Review: She ___ the squad ___ ___ hard tackles in training — the risk of injury was too high.",
        options: ["warned / not to attempt", "advised / not to attempt", "reminded / not to attempt"],
        a: "warned / not to attempt",
        tip: "Physical danger = warned + person + not to + infinitive."
      },
      {
        type: "mc",
        q: "Review: Choose the correct sentence:",
        options: [
          "She encouraged the midfielder to take on more responsibility.",
          "She encouraged to the midfielder to take on more responsibility.",
          "She encouraged the midfielder take on more responsibility."
        ],
        a: "She encouraged the midfielder to take on more responsibility.",
        tip: "Encouraged + person (no preposition) + to + infinitive. TO is required."
      },
      {
        type: "mc",
        q: "Review: Amara ___ players ___ what they couldn't do. She ___ them ___ what they already knew. She ___ them ___ what they'd never tried.",
        options: ["never ordered / reminded / to do / encouraged / to attempt", "never warned / asked / to do / persuaded / to try", "never told / reminded / to do / encouraged / to try"],
        a: "never ordered / reminded / to do / encouraged / to attempt",
        tip: "No commands = never ordered. Prior knowledge = reminded. Confidence + new challenge = encouraged.",
        successTip: "🎉 UNIT 11 COMPLETE! Asked ✅ Reminded ✅ Warned ✅ Ordered ✅ Encouraged ✅ Advised ✅ Persuaded ✅ Positive and negative infinitives ✅ Object placement ✅ Every verb tells a different story — and now you know exactly which one to use! 🏆⚽🌍"
      }
    ]
  }
};
